/* oxlint-disable react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md */
import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import {
    Body1,
    Button,
    Heading1,
    Heading2,
    Stack,
} from '@myphx-engineering/phx-design-system';

import { getFragment } from '../../graphql/client/helpers/get-fragment';
import {
    GetPrescriberOrderDocument,
    GetProviderDocument,
    SendPrescriberOrderDocument,
} from '../../graphql/generated/graphql';
import { usePatientContext } from '../../hooks/use-patient-context';
import { useRefetchQueries } from '../../hooks/use-refetch-queries';
import { getAbsoluteRoute } from '../../util/get-absolute-route';
import { getRefetchQueryForPrescription } from '../../util/get-refetch-query-for-prescription';
import { Loader } from '../common/Loader';
import { SendToPharmacyError } from '../errors/SendToPharmacyError';
import PatientMedicationDetails from '../pages/order-details/PatientMedicationDetails';
import { ProviderInfo } from '../provider/ProviderInfo';

const ProviderDetails = () => {
    const params = useParams();
    const { prescriberOrderId, providerId } = params;
    const { patient: userPatient } = usePatientContext();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [sendError, setSendError] = useState<boolean>(false);

    if (!prescriberOrderId || !providerId || !userPatient) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const { data, loading } = useQuery(GetPrescriberOrderDocument, {
        variables: { prescriberOrderId },
    });

    const { data: providerData, loading: providerLoading } = useQuery(
        GetProviderDocument,
        {
            variables: { id: providerId },
        }
    );

    const [sendPrescriberOrder, { loading: isSendLoading }] = useMutation(
        SendPrescriberOrderDocument
    );

    const {
        refetch,
        isComplete: isRefetchComplete,
        isLoading: isRefetchLoading,
    } = useRefetchQueries(
        getRefetchQueryForPrescription(prescriberOrderId, userPatient.id)
    );

    useEffect(() => {
        if (isRefetchComplete) {
            navigate(
                getAbsoluteRoute('order.prescriberOrderId.confirmation', {
                    prescriberOrderId,
                })
            );
        }
    }, [isRefetchComplete, navigate, prescriberOrderId]);

    const handleSend = useCallback(async () => {
        setSendError(false);
        try {
            const result = await sendPrescriberOrder({
                variables: { prescriberOrderId, providerId },
            });

            if (result.data?.sendPrescriberOrder?.success) {
                refetch();
            } else {
                setSendError(true);
            }
        } catch {
            setSendError(true);
        }
    }, [prescriberOrderId, providerId, sendPrescriberOrder, refetch]);

    if (loading || providerLoading) {
        return <Loader />;
    }

    const prescriberOrder = getFragment(data?.prescriberOrderV2);
    if (!prescriberOrder) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }
    if (prescriberOrder.__typename !== 'DigitallyReceivedPrescriberOrder') {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.verifyPatient', {
                    prescriberOrderId,
                })}
            />
        );
    }
    const patient = getFragment(prescriberOrder.patient);

    const authorizedMedication =
        prescriberOrder.authorizedMedication.__typename ===
        'AuthorizedMedication'
            ? getFragment(prescriberOrder.authorizedMedication)
            : null;
    const provider = getFragment(providerData?.individualProvider);

    if (!authorizedMedication || !provider) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    return (
        <>
            <SendToPharmacyError show={sendError} />
            <Stack gap="lg">
                <PatientMedicationDetails
                    patientName={patient.fullName}
                    prescriberOrder={prescriberOrder}
                />

                <Stack gap="xl">
                    <Heading1>{t('previewDetails.title')}</Heading1>

                    <Stack gap="sm">
                        <Heading2>
                            {t('previewDetails.pharmacy.header')}
                        </Heading2>
                        <Body1>
                            {t('providerDetails.pharmacy.description')}
                        </Body1>
                        <ProviderInfo provider={provider} />
                    </Stack>

                    <Button
                        size="lg"
                        onClick={handleSend}
                        loading={isSendLoading || isRefetchLoading}
                    >
                        {t('previewDetails.sendToPharmacy')}
                    </Button>
                </Stack>
            </Stack>
        </>
    );
};

export default ProviderDetails;
